import React, { useEffect, useRef, useState } from "react";
import { useFormModal } from "../../../components/Modals/formModal.js/FormModal";
import ReleaseList from "../Shared/ReleaseList";
import AutoCompletation from "../../../components/Common/AutoCompletation";
import { Col, ControlLabel, FormControl, FormGroup, Grid, Row, Panel, PanelGroup } from 'react-bootstrap';
import { Add, Edit, Remove } from "../../../components/Icons/Icons";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { useTranslation } from "react-i18next";
import { MoveItemRequest, MovementValidations } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import { CustomDate } from "../../../components/CustomControls/CustomDateTime";
import InventoryMedicationService from "../../../api/Inventory/InventoryMedicationService";
import { ReleaseExpirationWarning } from "../Shared/ExpirationWarning";
import { ErrorMessage } from "../../../components/Notifications/Notifications";
import InventoryStockService from "../../../api/Inventory/InventoryStockService";
import { buildMedicationLabel, LotOfferRule } from "../Shared/inventoryFormRules";
import { QuantityInput } from "../Shared/QuantityInput";

export default function PharmacyItemField({ index, disabled, assignmentType, assignId, handleSubmit, movementItem, hasActions = false, actionsDisbled, handleRemove, bsStyle, isDispensed, lotConsumption = null }) {
    const inventoryItem = movementItem || new MoveItemRequest();
    const { t } = useTranslation();
    const expirationDateRef = useRef();
    const expirationDateInputRef = useRef();
    const quantityRef = useRef();
    const medicationRef = useRef();
    const [selectedItem, setSelectedItem] = useState(inventoryItem?.item != null ? {
        ...inventoryItem?.item,
        inventoryItemId: inventoryItem?.item?.id ?? inventoryItem?.item?.inventoryItemId,
    } : null);
    const [selectedRelease, setSelectedRelease] = useState(inventoryItem?.release);
    const [quantity, setQuantity] = useState(inventoryItem?.quantity ?? 1);
    const [ReleaseModal, ReleaseActions] = useFormModal({ FormContent: ReleaseList })
    const [editQuantity, setEditQuantity] = useState(false);
    const [availableQuantity, setAvailableQuantity] = useState(0);

    useEffect(() => {
        GetStock()
    }, [(selectedRelease != null)])

    async function GetStock() {
        if (selectedItem != null && selectedRelease != null) {
            const stockResult = await InventoryStockService.FindStockQuantity({
                intentoryItemId: selectedItem.id,
                releaseId: selectedRelease.id,
                assignmentType: assignmentType,
                assignId: assignId
            });
            //Siempre espero que devuelva solo 1, ya que busco por lote
            const stockQuantity = stockResult.Value[0]?.quantity ?? 0;
            if (isDispensed) {
                setAvailableQuantity(stockQuantity + inventoryItem.quantity);
            } else {
                setAvailableQuantity(stockQuantity);
            }
        } else {
            setAvailableQuantity(0);
        }
    }

    const handleShowReleaseModal = async () => {
        await ReleaseActions.Show({
            title: t('inventory.commons.lotNumber'),
            onConfirmation: async (selected) => {
                setSelectedRelease(selected)
                quantityRef.current.focus()
            },
            initValue: { itemId: selectedItem.inventoryItemId, assignmentType: assignmentType, assignId: assignId, filteredFrom: lotConsumption, lotOfferRule: LotOfferRule.NotAlreadyTaken, includeExpired: false }
        })
    }

    const fetchItems = async (term) => {
        const result = await InventoryMedicationService.Find({ term: term, onlyLocal: true, pageSize: 100 })
        return result.Value
    }

    const curringSelected = (callback) => {
        return (selected) => {
            if (selected != null && selected.length > 0) callback(selected[0])
            else callback(null)
        }
    }

    const onCleanSelectedRelease = () => {
        setSelectedRelease(null)
        setAvailableQuantity(0)
        setQuantity(0)
    }

    const handleFormSubmit = (e) => {
        if (!isFormCompleted() && handleSubmit !== null) return

        const item = {
            item: selectedItem,
            release: selectedRelease,
            assignment: {
                assignmentType: assignmentType,
                assignId: assignId,
            },
            quantity: quantity,
            availableQuantity: availableQuantity
        }

        if (MovementValidations.ItemExpiresLessThanGap(item)) {
            ErrorMessage(t('inventory.dispense.errors.expirationLessThanTwoMonthsComment'))
            return;
        }

        handleSubmit(item)
        handleItemClean()
    }
    function isFormCompleted() {
        return selectedItem !== null
            && selectedRelease !== null
            && quantity > 0
    }

    function handleItemClean() {
        setSelectedItem(null)
        setSelectedRelease(null)
        setQuantity(0)
        setAvailableQuantity(0)
        medicationRef.current.clear()
    }
    function onHandleRemove() {
        handleRemove(inventoryItem)
    }
    function handleEditQuantity() {
        setEditQuantity(true)
    }


    return (<Panel bsStyle={bsStyle} key={`multi-item-${index}`} eventKey={`multi-item-${index}`}>
        <Panel.Heading>
            <Panel.Title >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Panel.Title toggle>
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                            {selectedItem != null ?
                                (<>
                                    {selectedItem.presentation + (quantity > 0 ? ` - Total: ${quantity}` : "")}
                                    {selectedRelease != null && <span style={{ marginRight: '1rem' }}>
                                        <ReleaseExpirationWarning release={selectedRelease} />
                                    </span>}
                                </>)
                                : "Nueva Medicacion"}
                        </div>
                    </Panel.Title>
                    {hasActions && <div>
                        <button type="button" className="btn btn-danger" disabled={actionsDisbled} onClick={onHandleRemove} style={{ marginRight: '.5rem' }}>
                            <Tooltip tooltip={t('commons.remove')} id={"tooltipedit-eliminarMedicamento"}>
                                <span aria-hidden="true"><Remove /></span>
                            </Tooltip>
                        </button>
                        {/* <button type="button" className="btn btn-info" disabled={actionsDisbled} onClick={handleEditQuantity}>
                            <Tooltip tooltip={t('buttons.edit')} id={"tooltipedit-editarMedicamento"}>
                                <span aria-hidden="true"><Edit /></span>
                            </Tooltip>
                        </button> */}
                    </div>}
                </div>
            </Panel.Title>
        </Panel.Heading>
        <Panel.Body collapsible>
            <div autoComplete="off">
                <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <AutoCompletation
                            id={'medication'}
                            ref={medicationRef}
                            labelKey={(option) => buildMedicationLabel(option)}
                            placeholder={t('inventory.dispense.findMedication')}
                            fetchOptions={fetchItems}
                            onChange={curringSelected(setSelectedItem)}
                            disabled={disabled || selectedRelease != null}
                            defaultSelectedOptions={selectedItem != null ? [selectedItem] : []}
                        />
                    </FormGroup>
                </div>
                <hr />
                {selectedRelease == null && <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <button type="button" className="btn btn-primary" disabled={disabled || selectedItem == null} onClick={_ => handleShowReleaseModal()}>
                            <Tooltip>
                                <Add /> {t('inventory.dispense.selectLotNumber')}
                            </Tooltip>
                        </button>
                    </FormGroup>
                </div>
                    || <div className='input-group-container'>
                        <FormGroup style={{ width: "100%" }}>
                            <input disabled={true} value={selectedRelease?.lotNumber} className="rbt-input-main form-control rbt-input" />
                        </FormGroup>

                        <FormGroup style={{ width: "100%" }} >
                            <CustomDate
                                dateRef={expirationDateRef}
                                inputRef={expirationDateInputRef}
                                name={`expirationDate`}
                                onChange={() => { }}
                                onBlur={() => { }}
                                value={selectedRelease?.expirationDate ?? null}
                                defaulValue={null}
                                dateFormat="DD/MM/YYYY"
                                disabled={true}
                                inputProps={{ placeholder: t('commons.expirationDate') }}
                            />
                        </FormGroup>

                        {!disabled && <FormGroup style={{ width: "100%" }}>
                            <button type="button" className="btn btn-danger" disabled={disabled || selectedRelease == null} onClick={onCleanSelectedRelease}>
                                <Tooltip tooltip={t('commons.clear')} id={"tooltipedit-limpiarpaciente"}>
                                    <span aria-hidden="true"><Remove /></span>
                                </Tooltip>
                            </button>
                        </FormGroup>}
                    </div>}
                <hr />
                <div className='input-group-container'>
                    <QuantityInput ref={quantityRef}
                        value={quantity}
                        onChange={setQuantity}
                        max={availableQuantity}
                        disabled={!editQuantity && (disabled || selectedItem == null || selectedRelease == null)}
                        placeholder={t('inventory.dispense.quantity')}
                        style={{ width: "50%" }} />
                </div>
                <div style={{ display: "flex", flexDirection: 'column', justifyContent: 'end' }} >
                    <div style={{ alignSelf: 'self-end', marginTop: '1rem' }}>
                        <button type="button" className='btn btn-success' disabled={!editQuantity && (disabled || !isFormCompleted())} onClick={_ => handleFormSubmit()}>{t('buttons.add')}</button>
                    </div>
                </div>
                <ReleaseModal />
            </div>
        </Panel.Body>
    </Panel>)
}